import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef } from 'react';

// Import styles
import { ClassList } from 'lib/utils/react/classlist';
import './autocomplete.scss';

// Default delay from last type to trigger onChange
const DELAY = 325;

// Props for the Autocomplete component
export interface AutocompleteProps<T> {
  name?: string;
  onSearch: (value: string) => Promise<AutocompleteItem<T>[]>;
  onSelect: (item: T) => void;
  onClear?: () => void;
  isClearOnSelect?: boolean;
  delay?: number;
  style?: React.CSSProperties;
  placeholder?: string;
}

// Autocomplete item
export interface AutocompleteItem<T> {
  value: number | string;
  label: string;
  description?: string;
  type?: string;
  isSelectable: boolean;
  object: T;
}

/**
 * An Autocomplete component that allows for searching and selecting items.
 *
 * @param props React component props
 */
export const Autocomplete = <T extends any>({
  isClearOnSelect = false,
  delay = DELAY,
  ...props
}: AutocompleteProps<T>) => {
  const uniqueId = useMemo(() => window.crypto.randomUUID(), []);

  const [value, setValue] = React.useState('');
  const [queryString, setQueryString] = React.useState(value);
  const [focusIndex, setFocusIndex] = React.useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setQueryString(value);
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value]);

  const query = useQuery({
    queryKey: ['autocomplete', uniqueId, queryString.trim()],
    queryFn: () => props.onSearch(queryString.trim()),
    enabled: queryString.trim() !== ''
  });

  const optionHeight = listRef?.current?.children[0]?.clientHeight ?? 0;

  const onClear = () => {
    setValue('');
    setQueryString('');
    setFocusIndex(-1);
    props.onClear ? props.onClear() : null; // Optional onClear
    inputRef.current?.focus();
  };

  const onSelect = (item: AutocompleteItem<T>) => {
    props.onSelect(item.object);
    if (isClearOnSelect) {
      onClear();
    }
  };

  const onSelectIndex = (index: number) => {
    if (query.isSuccess && query.data.length) {
      const items = query.data;
      if (index >= 0 && index < items.length && items[index].isSelectable) {
        onSelect(items[index]);
      }
    }
  };

  const scrollDown = () => {
    if (query.isSuccess && query.data.length) {
      setFocusIndex((prev) => Math.min(prev + 1, query.data.length - 1));
    }
  };

  const scrollUp = () => {
    if (query.isSuccess && query.data.length) {
      setFocusIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const keyOperation: { [key: string]: () => void } = {
      ArrowDown: scrollDown,
      ArrowUp: scrollUp,
      Enter: () => {
        e.preventDefault();
        e.stopPropagation();
        onSelectIndex(focusIndex);
      },
      Escape: onClear
    };
    if (keyOperation[e.key]) {
      keyOperation[e.key]();
    } else {
      setFocusIndex(-1);
    }
  };

  useEffect(() => {
    // Must have a successful result with at least one
    // item and the list must be rendered
    if (!query.isSuccess || !query.data.length || !listRef.current) {
      return;
    }

    if (focusIndex === -1 || focusIndex === 0) {
      listRef.current.scrollTop = 0;
      return;
    }

    if (focusIndex === query.data.length - 1) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
      return;
    }

    // Size calculations
    const padding = 4;
    const dropdownHeight = listRef.current.clientHeight;
    const scrollTop = listRef.current.scrollTop;

    // Current item
    const item: HTMLDivElement = listRef.current.children[focusIndex] as HTMLDivElement;
    if (!item) {
      return;
    }

    // Get current item's top and bottom offset within parent
    const itemTop = item.offsetTop;
    const itemBottom = item.offsetTop + item.clientHeight;

    // Check if item is below or above the view
    if (itemBottom > dropdownHeight + scrollTop) {
      listRef.current.scrollTop = itemBottom - dropdownHeight + padding;
    } else if (itemTop < scrollTop) {
      listRef.current.scrollTop = itemTop;
    }
  }, [queryString.trim(), query.isSuccess, focusIndex]);

  return (
    <div className="autocomplete-component" style={props.style}>
      <input
        name={props.name}
        type="text"
        value={value}
        placeholder={props.placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => onKeyDown(e)}
        ref={inputRef}
        role="searchbox"
        aria-autocomplete="list"
      />
      {query.isLoading ? (
        <div className="autocomplete-dropdown">
          <div className="autcomplete-status subtle">Loading...</div>
        </div>
      ) : null}
      {query.isSuccess && query.data.length == 0 ? (
        <div className="autocomplete-dropdown">
          <div className="autcomplete-status subtle">No results found</div>
        </div>
      ) : null}
      {query.isSuccess && query.data.length > 0 ? (
        <div className="autocomplete-dropdown" ref={listRef} role="listbox">
          {query.data.map((option, index) => {
            const classes = ClassList.of('autocomplete-item')
              .add('is-selectable', option.isSelectable)
              .add('is-focused', index === focusIndex);
            return (
              <div
                className={classes.names}
                key={option.value}
                onClick={() => {
                  if (option.isSelectable) {
                    onSelect(option);
                  }
                }}
                onMouseOver={() => setFocusIndex(index)}
                title={option.description}
                aria-label={option.label}
                aria-current={index === focusIndex ? 'true' : 'false'}
                role="option"
              >
                <span className="item-label">{option.label}</span>
                {option.type ? (
                  <>
                    <br />
                    <span className="item-type subtle">{option.type}</span>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
